/**
 * Email Service Utility
 * Provides functions to send emails via AWS SES for interview scheduling
 */

const EMAIL_ENDPOINT = "https://pharmaclick.co.in/RazorPay/WeatherForecast/sendEmailSES";
const API_KEY = "abce-124";

// Default sender information for GetScreened emails
const DEFAULT_FROM_NAME = "GetScreened";
const DEFAULT_FROM_EMAIL = "support@getscreened.in";

/**
 * Send an email using the SES service
 * @param {Object} params - Email parameters
 * @param {string} params.to - Recipient email address
 * @param {string} params.subject - Email subject
 * @param {string} params.body - Email body (HTML supported)
 * @param {string} params.fromName - Sender name (optional, defaults to GetScreened)
 * @param {string} params.fromEmail - Sender email (optional, defaults to support@getscreened.in)
 * @returns {Promise<Object>} Response from the email service
 */
export const sendEmail = async ({ to, subject, body, fromName = DEFAULT_FROM_NAME, fromEmail = DEFAULT_FROM_EMAIL }) => {
  if (!to || !subject || !body) {
    throw new Error('Email to, subject, and body are required');
  }

  const myHeaders = new Headers();
  myHeaders.append("X-Api-Key", API_KEY);
  myHeaders.append("Content-Type", "application/json");

  const raw = JSON.stringify({
    subject: subject,
    body: body,
    to: to,
    fromName: fromName,
    fromEmail: fromEmail,
  });

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: raw,
    redirect: "follow",
  };

  try {
    const response = await fetch(EMAIL_ENDPOINT, requestOptions);
    
    // Check HTTP status first
    if (!response.ok) {
      console.error(`Email service returned status ${response.status}`);
      const errorText = await response.text();
      console.error("Error response:", errorText);
      throw new Error(`Email service failed with status ${response.status}`);
    }
    
    const result = await response.json();

    if (result.message === "Invalid API Key") {
      console.error("Invalid API Key");
      throw new Error("Email service authentication failed");
    }

    if (result.message?.includes("SMTP server requires a secure connection")) {
      console.error("SMTP connection error");
      throw new Error("Email service connection failed");
    }

    console.log("Email sent successfully:", result);
    return result;
  } catch (error) {
    console.error("Error sending email:", error);
    throw error;
  }
};

/**
 * Send screening interview link email (for "Schedule for Later" option)
 * The candidate can take the interview anytime using this link
 * @param {Object} params - Parameters for the email
 * @param {string} params.candidateEmail - Recipient email
 * @param {string} params.candidateName - Candidate's name
 * @param {string} params.interviewLink - Direct interview link
 * @param {string} params.slotId - Interview slot ID for reference
 * @returns {Promise<Object>} Response from the email service
 */
export const sendScreeningScheduleEmail = async ({
  candidateEmail,
  candidateName,
  interviewLink,
  slotId,
}) => {
  const subject = "GetScreened - Your AI Screening Interview Link is Ready";
  
  const body = `
    <html>
      <body>
        <h2>Welcome to GetScreened!</h2>
        
        <p>Dear ${candidateName || 'Candidate'},</p>
        
        <p>Thank you for completing your profile on GetScreened! We're excited to help you showcase your skills to potential employers.</p>
        
        <p>Your <strong>AI Screening Interview</strong> is ready and waiting for you. Take it anytime that's convenient for you - it only takes about <strong>5 minutes</strong>!</p>
        
        <p><strong>Pro Tip:</strong> Take the interview when you're in a quiet place with good lighting and a stable internet connection.</p>
        
        <p><a href="${interviewLink}">Click here to Start Your Interview</a></p>
        
        <p>Or copy this link: ${interviewLink}</p>
        
        <h3>What to Expect:</h3>
        <ul>
          <li>5 AI-powered interview questions</li>
          <li>Based on your resume and skills</li>
          <li>Takes approximately 5 minutes</li>
          <li>Instant feedback after completion</li>
          <li>Available 24/7 - take it whenever you're ready!</li>
        </ul>
        
        <p>You can also access your interview anytime from your <a href="https://www.app.getscreened.in/">GetScreened Dashboard</a>.</p>
        
        <p>If you have any questions, please don't hesitate to reach out.</p>
        
        <p>Best regards,<br><strong>The GetScreened Team</strong></p>
        
        <hr>
        <p><small>GetScreened - <a href="https://www.getscreened.in">www.getscreened.in</a></small></p>
        ${slotId ? `<p><small>Reference: ${slotId}</small></p>` : ''}
      </body>
    </html>
  `;

  return sendEmail({
    to: candidateEmail,
    subject: subject,
    body: body,
  });
};

/**
 * Send screening interview reminder email
 * @param {Object} params - Parameters for the email
 * @param {string} params.candidateEmail - Recipient email
 * @param {string} params.candidateName - Candidate's name
 * @param {string} params.interviewLink - Direct interview link
 * @param {string} params.scheduledTime - Scheduled time for the interview
 * @returns {Promise<Object>} Response from the email service
 */
export const sendScreeningReminderEmail = async ({
  candidateEmail,
  candidateName,
  interviewLink,
  scheduledTime,
}) => {
  const subject = `Reminder: Your GetScreened AI Interview is Coming Up!`;
  
  const body = `
    <html>
      <body>
        <h2>Interview Reminder</h2>
        
        <p>Dear ${candidateName || 'Candidate'},</p>
        
        <p>This is a friendly reminder about your upcoming AI Screening Interview on GetScreened.</p>
        
        ${scheduledTime ? `<p><strong>Scheduled Time:</strong> ${scheduledTime}</p>` : ''}
        
        <p><a href="${interviewLink}">Click here to Start Your Interview</a></p>
        
        <p><strong>Pro Tips:</strong></p>
        <ul>
          <li>Find a quiet place with good lighting</li>
          <li>Ensure stable internet connection</li>
          <li>Keep your resume handy for reference</li>
        </ul>
        
        <p>Best of luck!<br><strong>The GetScreened Team</strong></p>
        
        <hr>
        <p><small>GetScreened - <a href="https://www.getscreened.in">www.getscreened.in</a></small></p>
      </body>
    </html>
  `;

  return sendEmail({
    to: candidateEmail,
    subject: subject,
    body: body,
  });
};

/**
 * Send screening completion confirmation email
 * @param {Object} params - Parameters for the email
 * @param {string} params.candidateEmail - Recipient email
 * @param {string} params.candidateName - Candidate's name
 * @param {string} params.dashboardLink - Link to view results
 * @returns {Promise<Object>} Response from the email service
 */
export const sendScreeningCompletionEmail = async ({
  candidateEmail,
  candidateName,
  dashboardLink = 'https://www.app.getscreened.in/',
}) => {
  const subject = "Congratulations! Your GetScreened Interview is Complete";
  
  const body = `
    <html>
      <body>
        <h2>Interview Completed!</h2>
        
        <p>Dear ${candidateName || 'Candidate'},</p>
        
        <p>Congratulations on completing your AI Screening Interview on GetScreened!</p>
        
        <p>Your interview has been evaluated and your results are now available on your dashboard.</p>
        
        <p><a href="${dashboardLink}">View Your Results</a></p>
        
        <h3>What's Next?</h3>
        <ul>
          <li>Share your profile with recruiters</li>
          <li>Apply to relevant job opportunities</li>
          <li>Continue building your skills</li>
        </ul>
        
        <p>Your feedback is invaluable! Help us improve: <a href="https://docs.google.com/forms/d/e/1FAIpQLScmRBD3N0Wblz9irjO5hDbSfS214vlrDbWIKJbOOG8Uc-acog/viewform">Share Feedback</a></p>
        
        <p>Best regards,<br><strong>The GetScreened Team</strong></p>
        
        <hr>
        <p><small>GetScreened - <a href="https://www.getscreened.in">www.getscreened.in</a></small></p>
      </body>
    </html>
  `;

  return sendEmail({
    to: candidateEmail,
    subject: subject,
    body: body,
  });
};

export default {
  sendEmail,
  sendScreeningScheduleEmail,
  sendScreeningReminderEmail,
  sendScreeningCompletionEmail,
};
